<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_crud_so-services"
    size="medium"
    :close-label="t('Cancelar')"
    :save-label="isDeleteMode ? (isDeleting ? t('Deletando') : t('Deletar')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="isDeleteMode ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <!-- Delete Confirmation Mode -->
      <div v-if="isDeleteMode" class="delete-confirmation">
        <p>{{ t('Tem certeza que deseja deletar este serviço?') }}</p>
        <p class="text-muted">{{ serviceToDelete?.so_service_name }}</p>
      </div>

      <!-- Create/Edit Mode -->
      <div v-else>
        <s-o-services-form
          ref="formRef"
          :serviceId="currentServiceId"
          :isEditMode="isEditMode"
          @submit-success="handleFormSuccess"
          @submit-error="handleFormError"
        />
        <div v-if="isEditMode" class="delete-action">
          <Button
            data-testid="so-services-delete-button"
            severity="danger"
            outlined
            icon="pi pi-trash"
            :label="t('Deletar')"
            @click="enterDeleteConfirmation"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { eventBus } from '../../eventBus';
import SOServicesForm from './SOServicesForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface SOServiceData {
  so_service_id?: Id;
  so_service_name?: string;
  [key: string]: unknown;
}

interface SOServicesFormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  getServiceName: () => string;
}

interface ModalOpenPayload {
  id?: Id;
  data?: SOServiceData | null;
  action?: 'create' | 'edit' | 'delete';
}

// Get i18n composable
const { t } = useI18n();

// Use composables
const { remove: deleteService } = useSOCatalog('services');

// Reactive data
const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentServiceId = ref<Id | null>(null);
const serviceToDelete = ref<SOServiceData | null>(null);
const formRef = ref<SOServicesFormInstance | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

// Computed properties
const modalTitle = computed(() => {
  if (isDeleteMode.value) {
    return t('Deletar Serviço');
  } else if (isEditMode.value) {
    return t('Editar Serviço');
  } else {
    return t('Adicionar Serviço');
  }
});

// Methods
const openForCreate = (): void => {
  log('SOServicesModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentServiceId.value = null;

  // Reset form after modal is visible
  nextTick(() => {
    if (formRef.value) {
      formRef.value.resetForm();
    }
  });
};

const openForEdit = async (serviceId: Id): Promise<void> => {
  log('SOServicesModal', 'openForEdit', 'Opening modal for edit', serviceId);
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentServiceId.value = serviceId;

  // Wait for the form component to be rendered
  await nextTick();

  // Explicitly load data for edit
  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const openForDelete = async (serviceId: Id, serviceData: SOServiceData | null = null): Promise<void> => {
  log('SOServicesModal', 'openForDelete', 'Opening modal for delete', serviceId);
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentServiceId.value = serviceId;
  serviceToDelete.value = serviceData;
};

const enterDeleteConfirmation = (): void => {
  log('SOServicesModal', 'enterDeleteConfirmation', 'Switching to delete confirmation', currentServiceId.value);
  serviceToDelete.value = { so_service_name: formRef.value?.getServiceName() ?? '' };
  isEditMode.value = false;
  isDeleteMode.value = true;
};

const handleClose = (): void => {
  log('SOServicesModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentServiceId.value = null;
  serviceToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;

  // Reset form
  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('SOServicesModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleDelete = async (): Promise<void> => {
  if (!currentServiceId.value) {
    logWarn('SOServicesModal', 'handleDelete', 'No service ID to delete');
    return;
  }

  log('SOServicesModal', 'handleDelete', 'Deleting service', currentServiceId.value);
  isDeleting.value = true;

  try {
    await deleteService({ so_service_id: currentServiceId.value });
    eventBus.emit('globalMessage.success', t('Serviço deletado com sucesso'));
    eventBus.emit('so-services.deleted');
    handleClose();
  } catch (error: unknown) {
    logError('SOServicesModal', 'handleDelete', 'Error deleting service', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao deletar serviço') + ': ' + message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (): void => {
  log('SOServicesModal', 'handleFormSuccess', 'Form submitted successfully');
  eventBus.emit('so-services.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SOServicesModal', 'handleFormError', 'Form submission error', error);
  // Error message is already shown by the form component
};

// Standard modal open handler (for modal.so-services.open event)
const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('SOServicesModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.action === 'delete' && data?.id) {
    await openForDelete(data.id, data.data || null);
  } else if (data?.id) {
    await openForEdit(data.id);
  } else {
    openForCreate();
  }
};

// Lifecycle - Event listeners
onMounted(() => {
  log('SOServicesModal', 'onMounted', 'Component mounted, setting up event listeners');

  // Standard modal events (preferred pattern)
  eventBus.on<ModalOpenPayload>('modal.so-services.open', handleModalOpen);
  eventBus.on('modal.so-services.close', handleClose);
});

onBeforeUnmount(() => {
  log('SOServicesModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  // Remove standard modal events
  eventBus.off<ModalOpenPayload>('modal.so-services.open', handleModalOpen);
  eventBus.off('modal.so-services.close', handleClose);
});

// Expose methods for parent components (legacy support)
defineExpose({
  openForCreate,
  openForEdit,
  openForDelete
});
</script>

<style scoped>
.delete-confirmation {
  padding: 20px;
  text-align: center;
}

.delete-confirmation p {
  margin-bottom: 10px;
  font-size: 16px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}

.delete-action {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--app-bg-dark, #ccc);
}
</style>
